import { addDays } from 'date-fns'

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { AlertOIcon } from '@rfh-core/icons'
import theme from '@rfh-core/theme'

import {
  formatDate,
  IBuyersCardView,
  toCetDateOrDefault,
  toCetDateStringOrDefault,
} from 'src/common'
import BooleanTag from 'src/common/components/BooleanTag'
import i18n from 'src/i18n'

export const getBuyerCardColumns = (sortingAllowed: boolean): GridColDef[] => [
  {
    field: 'customerRfhRelationId',
    headerName: i18n.t('buyersCards.customerNumber'),
    sortable: sortingAllowed,
    align: 'right',
    headerAlign: 'right',
    flex: 1,
    hideable: false,
  },
  {
    field: 'customrRfhRelationName',
    headerName: i18n.t('buyersCards.customerName'),
    flex: 3,
    sortable: sortingAllowed,
  },
  {
    field: 'buyersCardId',
    headerName: i18n.t('buyersCards.plateNumber'),
    align: 'right',
    headerAlign: 'right',
    flex: 1,
    sortable: sortingAllowed,
  },
  {
    field: 'aalsmeer',
    headerName: 'A',
    type: 'boolean',
    align: 'center',
    headerAlign: 'center',
    flex: 0.6,
    sortable: false,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) => (
      <BooleanTag value={params.row.aalsmeer} />
    ),
  },
  {
    field: 'naaldwijk',
    headerName: 'N',
    type: 'boolean',
    align: 'center',
    headerAlign: 'center',
    flex: 0.6,
    sortable: false,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) => (
      <BooleanTag value={params.row.naaldwijk} />
    ),
  },
  {
    field: 'rijnsburg',
    headerName: 'R',
    type: 'boolean',
    align: 'center',
    headerAlign: 'center',
    flex: 0.6,
    sortable: false,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) => (
      <BooleanTag value={params.row.rijnsburg} />
    ),
  },
  {
    field: 'eelde',
    headerName: 'E',
    type: 'boolean',
    align: 'center',
    headerAlign: 'center',
    flex: 0.6,
    sortable: false,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) => (
      <BooleanTag value={params.row.eelde} />
    ),
  },
  {
    field: 'startDate',
    headerName: i18n.t('buyersCards.startDate'),
    type: 'date',
    sortable: sortingAllowed,
    flex: 1.5,
    valueGetter: ({ value }) => toCetDateOrDefault(value),
    valueFormatter: ({ value }) =>
      toCetDateStringOrDefault(value, i18n.t('common.dateFormatShort')),
    renderCell: ({ value }) =>
      formatDate(value, i18n.t('common.dateFormatShort'), 'CET'),
  },
  {
    field: 'endDate',
    headerName: i18n.t('buyersCards.endDate'),
    type: 'date',
    sortable: sortingAllowed,
    flex: 1.5,
    valueGetter: ({ value }) => toCetDateOrDefault(value),
    valueFormatter: ({ value }) =>
      toCetDateStringOrDefault(value, i18n.t('common.dateFormatShort')),
    renderCell: ({ value }) =>
      formatDate(value, i18n.t('common.dateFormatShort'), 'CET'),
  },
  {
    field: 'releasedDateTime',
    headerName: i18n.t('buyersCards.releaseDate'),
    type: 'date',
    sortable: sortingAllowed,
    flex: 1.5,
    valueGetter: ({ value }) => toCetDateOrDefault(value),
    valueFormatter: ({ value }) =>
      toCetDateStringOrDefault(value, i18n.t('common.dateFormatShort')),
    renderCell: ({ value }) =>
      formatDate(value, i18n.t('common.dateFormatShort'), 'CET'),
  },
  {
    field: 'blockedDateTime',
    headerName: i18n.t('buyersCards.blockDate'),
    type: 'date',
    sortable: sortingAllowed,
    flex: 1.5,
    valueGetter: ({ value }) => toCetDateOrDefault(value),
    valueFormatter: ({ value }) =>
      toCetDateStringOrDefault(value, i18n.t('common.dateFormatShort')),
    renderCell: ({ value }) =>
      formatDate(value, i18n.t('common.dateFormatShort'), 'CET'),
  },
  {
    field: 'isVervallen',
    type: 'boolean',
    headerName: i18n.t('buyersCards.expired'),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    sortable: sortingAllowed,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) =>
      params.row.isVervallen ? (
        <AlertOIcon
          sx={{
            color: `${theme.rfhColors.alertRed}`,
          }}
        />
      ) : (
        '-'
      ),
  },
  {
    field: 'blockedindication',
    type: 'boolean',
    headerName: i18n.t('buyersCards.blocked'),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    sortable: sortingAllowed,
    valueFormatter: ({ value }) => value, // Needed for csv exports to supply the boolean as `Yes` and `No`, it uses the value from the valueGetter
    valueGetter: ({ value }) =>
      value ? i18n.t('common.yes') : i18n.t('common.no'), // Needed for excel exports to supply the boolean as `Yes` and `No`
    renderCell: (params: GridRenderCellParams) =>
      params.row.blockedindication ? (
        <AlertOIcon
          sx={{
            color: `${theme.rfhColors.alertRed}`,
          }}
        />
      ) : (
        '-'
      ),
  },
  {
    field: 'remark',
    headerName: i18n.t('common.remark'),
    align: 'left',
    headerAlign: 'left',
    flex: 1.5,
    sortable: sortingAllowed,
  },
]

export const defaultBuyerscard: IBuyersCardView = {
  customerRfhRelationId: undefined,
  buyersCardId: undefined,
  aalsmeer: false,
  naaldwijk: false,
  rijnsburg: false,
  eelde: false,
  creationUser: undefined,
  creationDateTime: new Date(),
  mutationDateTime: new Date(),
  mutationUser: undefined,
  blockedDateTime: undefined,
  isVervallen: false,
  releasedDateTime: undefined,
  blockedindication: false,
  remark: undefined,
  startDate: addDays(new Date(), 1),
  endDate: new Date('2099-12-31T23:59:59'),
}
